import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { stickerPieces } from '../utils/mapOverlay';
import { snapStickerCenter } from '../../../shared/mapLayout.mjs';
import useTokenRotationShortcuts from '../hooks/useTokenRotationShortcuts';
import classNames from '../../../utils/classNames';

// As dark as the night tint the finished map is drawn with.
const NIGHT_FILTER = 'brightness(0.5)';
// How far a key press turns the chosen sticker, as for a sticker in the Map Maker.
const TURN_STEP = 15;

// Stickers over the map and under the tokens, each where it lies and turned as it lies. They come from the map's saved
// layout (or `layout`, with moves still on their way), not its picture, so they can be moved without redrawing the map.
// Clicks pass through to the tokens, except while the DM is moving stickers (`editing`): then a sticker is chosen by
// pressing on it, dragged, and turned with the unit keys, and `onMove` gets where it was put.
const MapStickers = ({ map, catalog, layout: shownLayout, editing, selectedId, onSelect, onMove }) => {
  const layout = shownLayout || map?.layout;
  const layerRef = useRef(null);
  const drag = useRef(null);
  const [dragged, setDragged] = useState(null);
  const stickers = useMemo(() => {
    const placed = layout?.stickers || [];
    return dragged ? placed.map((sticker) => (sticker.id === dragged.id ? { ...sticker, x: dragged.x, y: dragged.y } : sticker)) : placed;
  }, [layout, dragged]);
  const pieces = useMemo(() => stickerPieces(layout && { ...layout, stickers }, catalog), [layout, stickers, catalog]);
  const chosen = editing ? stickers.find(({ id }) => id === selectedId) : null;
  const turnBy = useCallback((id, step) => {
    const sticker = (layout?.stickers || []).find((entry) => entry.id === id);
    if (sticker) onMove?.(id, { x: sticker.x, y: sticker.y, turn: (((sticker.turn + step) % 360) + 360) % 360 });
  }, [layout, onMove]);
  const letGo = useCallback(() => onSelect?.(null), [onSelect]);
  useTokenRotationShortcuts(chosen?.id, turnBy, letGo, TURN_STEP);
  if (!pieces.length) return null;

  // A pointer's place on the map, in squares.
  const pointAt = (event) => {
    const rect = layerRef.current?.getBoundingClientRect();
    if (!rect?.width || !rect?.height) return null;
    return { x: ((event.clientX - rect.left) / rect.width) * layout.columns, y: ((event.clientY - rect.top) / rect.height) * layout.rows };
  };
  const press = (event, sticker) => {
    const point = pointAt(event);
    if (!point || event.button > 0) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drag.current = { sticker, dx: sticker.x - point.x, dy: sticker.y - point.y };
    onSelect?.(sticker.id);
  };
  const follow = (event) => {
    const point = drag.current && pointAt(event);
    if (!point) return;
    const { sticker, dx, dy } = drag.current;
    const at = snapStickerCenter(layout, sticker.kind, { x: point.x + dx, y: point.y + dy }, sticker.turn);
    setDragged(at.x === sticker.x && at.y === sticker.y ? null : { id: sticker.id, ...at });
  };
  const release = (event) => {
    if (!drag.current) return;
    event.stopPropagation();
    const { sticker } = drag.current;
    drag.current = null;
    if (dragged?.id === sticker.id) onMove?.(sticker.id, { x: dragged.x, y: dragged.y, turn: sticker.turn });
    setDragged(null);
  };
  const handlers = (sticker) => (editing ? {
    onPointerDown: (event) => press(event, sticker), onPointerMove: follow, onPointerUp: release, onPointerCancel: () => { drag.current = null; setDragged(null); },
  } : {});

  return (
    <div ref={layerRef} className={classNames('campaign-map-board__stickers', editing && 'campaign-map-board__stickers--editing')}
      style={layout.lighting === 'night' ? { filter: NIGHT_FILTER } : undefined} aria-hidden="true">
      {pieces.map(({ key, kind, label, url, left, top, width, height, turn, fit }) => {
        const sticker = stickers.find(({ id }) => id === key);
        const style = { left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%`, transform: `translate(-50%, -50%) rotate(${turn}deg)`, ...(fit ? { objectFit: fit } : {}) };
        const shared = { style, 'data-sticker-id': key, ...handlers(sticker),
          className: classNames('campaign-map-board__sticker', !url && 'campaign-map-board__sticker--stand-in', chosen?.id === key && 'campaign-map-board__sticker--selected') };
        return url
          ? <img key={key} {...shared} src={url} alt="" draggable={false} />
          : <div key={key} {...shared} data-sticker-stand-in={kind}>{label}</div>;
      })}
    </div>
  );
};

MapStickers.propTypes = {
  map: PropTypes.shape({ layout: PropTypes.object }),
  catalog: PropTypes.object,
  layout: PropTypes.object,
  editing: PropTypes.bool,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func,
  onMove: PropTypes.func,
};

export default MapStickers;
